import * as Sentry from '@sentry/nextjs';
import { hasReloadedForBuild } from '@/lib/deployment-skew-state';
import { staticAssetPath } from './static-asset-path';

const pending = new Set<Promise<void>>();

/** Resource error events do not bubble, and the trace that records a 404 is sampled. */
export function installStaticAssetErrors(): () => void {
  const seen = new Set<string>();
  function onError(event: Event) {
    const target = event.target;
    const source =
      target instanceof HTMLScriptElement
        ? target.src
        : target instanceof HTMLLinkElement && target.rel === 'stylesheet'
          ? target.href
          : null;
    if (!source) {
      return;
    }
    const url = new URL(source, window.location.href);
    if (!staticAssetPath(source) || seen.has(url.pathname)) {
      return;
    }
    seen.add(url.pathname);
    const report = reportFailure(url).finally(() => pending.delete(report));
    pending.add(report);
  }
  window.addEventListener('error', onError, true);
  return () => window.removeEventListener('error', onError, true);
}

async function reportFailure(url: URL): Promise<void> {
  // Classify the original failure, not a later pod response from the HEAD probe.
  const persistent = hasReloadedForBuild();
  const entries = performance.getEntriesByName?.(url.href) ?? [];
  const timing = entries.at(-1) as PerformanceResourceTiming | undefined;
  const status = timing?.responseStatus || 'unknown';
  let probeStatus: number | 'unknown' = 'unknown';
  let servingRelease = 'unknown';
  try {
    // This is a diagnostic HEAD, not a retry of the script or a replay of an
    // action. Its pod may differ from the original request during a rollout.
    const response = await fetch(url.pathname, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'error',
      signal: AbortSignal.timeout(1500),
      keepalive: true,
    });
    probeStatus = response.status;
    const release = response.headers.get('x-hire-go-release');
    if (release && /^[\w.-]{1,100}$/.test(release)) {
      servingRelease = release;
    }
  } catch {
    // A network failure still reports; do not invent an HTTP status or release.
  }
  Sentry.captureException(new Error('Hire Go static asset failed to load'), {
    level: persistent ? 'error' : 'warning',
    fingerprint: [
      'browser',
      'static-asset-load',
      persistent ? 'persistent' : 'rollout',
    ],
    tags: {
      feature: 'static-asset-load',
      asset_path: url.pathname,
      asset_status: status,
      probe_status: probeStatus,
      tab_build: process.env.NEXT_PUBLIC_WAVE_GIT_COMMIT_SHA ?? 'unknown',
      serving_release: servingRelease,
    },
  });
}

/** Let diagnostics finish and enter the SDK queue before a recovery unloads the page. */
export async function waitForStaticAssetReports(): Promise<void> {
  await Promise.all(pending);
}
